import {
    useCallback,
    useEffect,
    useState,
} from 'react'
import {handleErrorResponse} from 'swayok-react-mdb-ui-kit/helpers/api/ApiRequestErrorHelpers'
import {useEventCallback} from 'swayok-react-mdb-ui-kit/helpers/useEventCallback'
import {useUrlQueryParams} from 'swayok-react-mdb-ui-kit/helpers/useUrlQueryParams'
import type {ReactSetState} from 'swayok-react-mdb-ui-kit/types'
import {useUserData} from '../../../../../hooks'
import {ChatsApi} from '../../ChatsApi'
import type {
    ChatDetailsData,
    ChatsFilter,
    ChatsListUrlQueryParams,
    ChatUserData,
    NewChatMessageEventData,
} from '../../ChatsModuleTypes'

interface useChatsPageContentApiConfig {
    miniChat?: boolean
    // Открывает в мини-чате определенный чат (по запросу извне).
    selectedChatId?: null | number
    // Вызывается после обработки свойства selectedChatId.
    resetSelectedChatProp?: () => void
}

interface useChatsPageContentApiHookReturn {
    userId: number | null
    chats: ChatDetailsData[] | null
    isProcessing: boolean
    isAllChatsLoaded: boolean
    filter: ChatsFilter | null
    chatForReview: ChatDetailsData | null
    isAddReviewAboutSellerModalVisible: boolean | null

    selectedChat: ChatDetailsData | number | null
    onChatSelect: (chat: ChatDetailsData | null) => void
    deleteChats: (chatsIds: number[]) => void
    readAllMessagesInChats: (chatsIds: number[]) => void
    showAddReviewModal: (chat: ChatDetailsData) => void
    onChatsListUpdate: (filter: ChatsFilter, chats: ChatDetailsData[], isAllChatsLoaded: boolean) => void
    receivedNewChatMessage: (eventData: NewChatMessageEventData) => void
    setIsAddReviewAboutSellerModalVisible: ReactSetState<boolean | null>
    setChatForReview: ReactSetState<ChatDetailsData | null>
    onReviewAdded: () => void
    onLatestMessagesLoaded: (chat: ChatDetailsData) => void
}

export function useChatsPageContentApi(
    config: useChatsPageContentApiConfig
): useChatsPageContentApiHookReturn {
    // Авторизованный пользователь.
    const userId = useUserData()?.id ?? null

    const {
        miniChat,
        selectedChatId,
        resetSelectedChatProp,
    } = config
    // Параметры в URL Query.
    const {
        urlQueryParams,
        setUrlQueryParams,
    } = useUrlQueryParams<ChatsListUrlQueryParams>()

    // Список чатов.
    const [
        chats,
        setChats,
    ] = useState<ChatDetailsData[] | null>(null)
    // Фильтр сообщений.
    const [
        filter,
        setFilter,
    ] = useState<ChatsFilter>(null)
    // Загружены все чаты?
    const [
        isAllChatsLoaded,
        setIsAllChatsLoaded,
    ] = useState<boolean>(false)
    // Выбранный чат.
    const chatIdInUrlQuery = miniChat ? 0 : parseInt(urlQueryParams.get('chat') || '0')
    const [
        selectedChat,
        setSelectedChat,
    ] = useState<ChatDetailsData | number | null>(
        chatIdInUrlQuery > 0 ? chatIdInUrlQuery : null
    )
    // Видимость окна отправки отзыва о продавце.
    const [
        isAddReviewAboutSellerModalVisible,
        setIsAddReviewAboutSellerModalVisible,
    ] = useState<boolean | null>(null)
    // Чат, по которому отправляется отзыв о продавце.
    const [
        chatForReview,
        setChatForReview,
    ] = useState<ChatDetailsData | null>(null)

    // Состояние выполнения действия с чатом.
    const [
        isProcessing,
        setIsProcessing,
    ] = useState<boolean>(false)

    // Список чатов обновлен - сохраняем.
    const onChatsListUpdate = useCallback((
        filter: ChatsFilter,
        chats: ChatDetailsData[],
        isAllChatsLoaded: boolean
    ): void => {
        setChats(chats)
        setIsAllChatsLoaded(isAllChatsLoaded)
        setFilter(filter)
    }, [])

    /*
     * Переход к сообщениям конкретного чата.
     * Установка нового значения chat (кроме null) в URL query происходит в <ChatDetailsCard>.
     */
    const onChatSelect = useEventCallback((
        chat: ChatDetailsData | null
    ) => {

        setSelectedChat(chat)
        if (
            !miniChat
            && !chat
            && urlQueryParams.get('chat') !== null
        ) {
            // Удаляем значение chat из URL query т.к. чат сброшен.
            urlQueryParams.delete('chat')
            setUrlQueryParams(urlQueryParams)
        }
    })

    /*
     * Событие: получено новое сообщение в чат.
     * useCallback уменьшает количество переподписок на события, т.к. иначе при
     * каждой перерисовке будет создаваться новая функция, которая в свою очередь
     * будет пересоздавать подписку на события. Поэтому пересоздание функции будет
     * происходить только при изменении списка чатов или пользователя
     * (только эти данные используются в функции).
     */
    const receivedNewChatMessage = useEventCallback((
        eventData: NewChatMessageEventData
    ): void => {
        setChats(chats => {
            if (!chats) {
                // Чаты еще не загружены: нет смысла обрабатывать событие.
                return chats
            }
            for (let i = 0; i < chats.length; i++) {
                const chat: ChatDetailsData = chats[i]
                if (chat.id === eventData.chat.id) {
                    // Чат уже есть в списке: обновляем часть данных.
                    chat.lastMessage = eventData.message.message
                    chat.lastMessageCreatedAt = eventData.message.createdAt
                    if (eventData.message.user.id !== userId) {
                        // Сообщение пришло от другого пользователя.
                        chat.hasNotViewedMessages = true
                    }
                    // Обновляем состояние списка чатов.
                    return chats.slice()
                }
            }
            // Чат не найден в списке: добавляем.
            let otherUser: ChatUserData
            if (eventData.message.user.id !== userId) {
                // Сообщение пришло от другого пользователя.
                otherUser = eventData.message.user
            } else {
                // Сообщение пришло от текущего пользователя.
                otherUser = eventData.chat.initiator.id !== userId
                    ? eventData.chat.initiator
                    : (eventData.chat.ad ? eventData.chat.ad.owner : eventData.chat.executor!.owner)
            }
            const newChat: ChatDetailsData = {
                ...eventData.chat,
                lastMessage: eventData.message.message,
                lastMessageCreatedAt: eventData.message.createdAt,
                otherUser,
                hasNotViewedMessages: eventData.message.user.id !== userId,
                otherUserHasDeletedChat: false,
                canPostReviewAboutSeller: false,
                canPostReviewAboutExecutor: false,
                reviewType: 'seller',
            }
            // Обновляем состояние списка чатов.
            return [newChat].concat(chats)
        })
    })

    // Отметить чат прочитанным.
    const readAllMessagesInChats = useEventCallback((
        chatsIds: number[]
    ): void => {
        setIsProcessing(true)
        ChatsApi.readAllMessagesInChats(chatsIds)
            .then(() => {
                // Помечаем соответствующие чаты в списке прочитанными.
                setChats(chats => {
                    if (chats && chats.length) {
                        const updates = chats.slice()
                        for (let i = 0; i < chats.length; i++) {
                            if (chatsIds.includes(chats[i].id)) {
                                updates[i].hasNotViewedMessages = false
                            }
                        }
                        return updates
                    }
                    return chats
                })
                setSelectedChat(selectedChat => {
                    // Помечаем текущий чат прочитанным, если ID совпадают.
                    if (
                        selectedChat !== null
                        && typeof selectedChat === 'object'
                        && chatsIds.includes(selectedChat.id)
                    ) {
                        return {...selectedChat, hasNotViewedMessages: false}
                    }
                    return selectedChat
                })
            })
            .catch(handleErrorResponse)
            .finally(() => {
                setIsProcessing(false)
            })
    })

    // Удалить чат.
    const deleteChats = useEventCallback((
        chatsIds: number[]
    ): void => {
        setIsProcessing(true)
        ChatsApi.deleteChats(chatsIds)
            .then(() => {
                setChats(chats => {
                    // Удаляем чаты из списка.
                    if (chats && chats.length) {
                        const updatedChats: ChatDetailsData[] = []
                        for (let i = 0; i < chats.length; i++) {
                            if (!chatsIds.includes(chats[i].id)) {
                                updatedChats.push(chats[i])
                            }
                        }
                        return updatedChats
                    }
                    return chats
                })

                // Выходим из удаленного чата.
                if (
                    selectedChat !== null
                    && typeof selectedChat === 'object'
                    && chatsIds.includes(selectedChat.id)
                ) {
                    onChatSelect(null)
                }
            })
            .catch(handleErrorResponse)
            .finally(() => {
                setIsProcessing(false)
            })
    })

    // Показать окно добавления отзыва о продавце.
    const showAddReviewModal = useCallback((
        chat: ChatDetailsData
    ): void => {
        setChatForReview(chat)
        setIsAddReviewAboutSellerModalVisible(true)
    }, [])

    // Обработка добавления отзыва на продавца.
    const onReviewAdded = useEventCallback(() => {
        if (chatForReview) {
            setChats(chats => {
                if (chats) {
                    // Обновляем флаг canPostReviewAboutSeller/Executor в списке чатов.
                    for (let i = 0; i < chats.length; i++) {
                        if (chats[i].id === chatForReview.id) {
                            const updatedChats = chats.slice()
                            updatedChats[i].canPostReviewAboutSeller = false
                            updatedChats[i].canPostReviewAboutExecutor = false
                            return updatedChats
                        }
                    }
                }
                return chats
            })
            // Обновляем флаг canPostReviewAboutSeller/Executor в выбранном чате.
            setSelectedChat(selectedChat => {
                if (
                    selectedChat
                    && typeof selectedChat === 'object'
                    && selectedChat.id === chatForReview.id
                ) {
                    return {
                        ...selectedChat,
                        canPostReviewAboutSeller: false,
                        canPostReviewAboutExecutor: false,
                    }
                }
                return selectedChat
            })
            setIsAddReviewAboutSellerModalVisible(false)
        } else {
            // Так нужно, чтобы в if ветке отработал batching.
            setIsAddReviewAboutSellerModalVisible(false)
        }
    })

    const onChatMessagesLoaded = useEventCallback((
        chat: ChatDetailsData
    ) => {
        setChats(chats => {
            if (chats && chats.length) {
                // Обновляем флаг hasNotViewedMessages в выбранном чате.
                for (let i = 0; i < chats.length; i++) {
                    if (chats[i].id === chat.id) {
                        const updatedChats = chats.slice()
                        updatedChats[i] = {
                            ...updatedChats[i],
                            hasNotViewedMessages: false,
                        }
                        return updatedChats
                    }
                }
            }
            return chats
        })
    })

    // Обработка изменения ID чата в URL Query.
    useEffect(() => {
        if (miniChat) {
            // Не нужно отслеживать изменения в URL Query для мини-чата.
            return
        }
        const chatId: string | null = urlQueryParams.get('chat')
        if (!chatId) {
            // Возврат к списку чатов.
            if (selectedChat) {
                setSelectedChat(null)
                if (urlQueryParams.has('chat')) {
                    urlQueryParams.delete('chat')
                    setUrlQueryParams(urlQueryParams)
                }
            }
            return
        }

        const chatIdInt = parseInt(chatId)
        if (isNaN(chatIdInt) || chatIdInt <= 0) {
            // Некорректное значение.
            setSelectedChat(null)
            // Убираем chat из URL query.
            urlQueryParams.delete('chat')
            setUrlQueryParams(urlQueryParams)
            return
        }
        if (
            selectedChat
            && (
                selectedChat === chatIdInt
                || (typeof selectedChat === 'object' && selectedChat.id === chatIdInt)
            )
        ) {
            // Тот же чат, что сохранен в состоянии: ничего не делаем.
            return
        }
        // Задаем новый чат
        setSelectedChat(chatIdInt)
    }, [urlQueryParams.get('chat')])

    // Обработка изменения selectedChatId.
    useEffect(() => {
        if (selectedChatId) {
            setSelectedChat(selectedChatId)
            resetSelectedChatProp?.()
            if (chats) {
                let hasChatInList = false
                for (let i = 0; i < chats.length; i++) {
                    if (chats[i].id === selectedChatId) {
                        hasChatInList = true
                        break
                    }
                }
                if (!hasChatInList) {
                    // Сброс списка чатов т.к. запрошенный чат не найден в текущем списке.
                    setTimeout(() => setChats(null), 1000)
                }
            }
        }
    }, [selectedChatId])

    return {
        userId,
        chats,
        isProcessing,
        isAllChatsLoaded,
        filter,
        chatForReview,
        isAddReviewAboutSellerModalVisible,
        setIsAddReviewAboutSellerModalVisible,

        selectedChat,
        onChatSelect,
        deleteChats,
        readAllMessagesInChats,
        showAddReviewModal,
        onChatsListUpdate,
        receivedNewChatMessage,
        onReviewAdded,
        setChatForReview,
        onLatestMessagesLoaded: onChatMessagesLoaded,
    }
}
